import { type ReactNode, useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router";
import type { ApiKeyInfo, SettingsResponse } from "../../shared/types";
import { DEFAULT_IDLE_MINUTES } from "../../shared/tags";
import { useLayout } from "../components/season/layout";
import { Button } from "../components/ui/Button";
import { cx } from "../components/ui/cx";
import { Icon, type IconName } from "../components/ui/Icon";
import { PageHeader } from "../components/ui/PageHeader";
import { Pill, type PillTone } from "../components/ui/Pill";
import { api } from "../lib/api";
import { opensLabel } from "../setup/AddressStep";
import { LOOKS, lookName, lookOf } from "../setup/looks";
import { shownHost } from "../setup/parts";
import { nextSetupPath, setupCount } from "../setup/SetupContext";
import { trackParts } from "../setup/trackParts";
import { useSettingsData } from "../setup/useSettingsData";
import "../styles/setup.css";
import { BrandPane } from "./BrandPane";
import { teamOf } from "./automations";
import { ApiPane, AutomationsPane, DomainsPane, ScriptsPane } from "./panes";
import { SeasonSettings } from "./SeasonSettings";
import { pausedNow, SendingPane, sendingHealthLine } from "./SendingPane";

export type PaneKey = "brand" | "sending" | "domains" | "scripts" | "automations" | "api";

export const SNAV: Array<{ key: PaneKey; label: string; icon: IconName }> = [
  { key: "brand", label: "Brand", icon: "palette" },
  { key: "sending", label: "Sending email", icon: "mail" },
  { key: "domains", label: "Web address", icon: "globe" },
  { key: "scripts", label: "Tracking", icon: "code" },
  { key: "automations", label: "Automations", icon: "zap" },
  { key: "api", label: "API keys", icon: "key" },
];

/** The setup step each page finishes, and where in setup it is done. */
export const SET_STEP: Partial<Record<PaneKey, ["look" | "email" | "address" | "tracking", string]>> = {
  brand: ["look", "/setup/look"],
  sending: ["email", "/setup/email"],
  domains: ["address", "/setup/address"],
  scripts: ["tracking", "/setup/tracking"],
};

// Why the old names: links in emails and the sending banner still carry `/settings#<pane>`.
const OLD_HASH: Record<string, PaneKey> = { brand: "brand", sending: "sending", domains: "domains", scripts: "scripts", automations: "automations", ingest: "api" };

export const stepUnset = (k: PaneKey, d: SettingsResponse) => {
  const step = SET_STEP[k];
  return !!step && !d.setup[step[0]];
};

/** One reading of each page's state for the cards, the side list and the page itself: a step not done says so. */
export function paneStatus(k: PaneKey, d: SettingsResponse, keys: { work: boolean } | null): [PillTone, string] {
  if (k === "sending" && pausedNow(d.sending)) return ["bad", "Paused"];
  if (stepUnset(k, d)) return ["warn", "Not set up yet"];
  if (k === "sending") return ["good", "Verified"];
  if (k === "domains") return ["good", d.domain.custom_domain_status === "verified" ? "Own address" : "Active"];
  if (k === "scripts") {
    const parts = trackParts(d);
    const n = parts.filter((p) => p.state === "loaded").length;
    const w = parts.filter((p) => p.state === "waiting").length;
    return n ? ["good", `${n} loaded`] : w ? ["plain", "Waiting for a visit"] : ["plain", "Skipped"];
  }
  if (k === "api") return keys?.work ? ["good", "Active"] : ["plain", "No key yet"];
  return ["good", "Set"];
}

/** The business's settings: cards for each part at `/settings`, and each part's page at `/settings/<part>`. */
export function SettingsView() {
  const { pane, sub } = useParams();
  const { hash } = useLocation();
  const { data, failed, run, setData } = useSettingsData();
  const layout = useLayout();
  const old = OLD_HASH[hash.replace(/^#/, "")];
  if (!pane && old) return <Navigate to={`/settings/${old}`} replace />;
  const key = SNAV.find((p) => p.key === pane)?.key ?? null;
  if (pane && !key) return <Navigate to="/settings" replace />;
  // Why: the records have a page of their own only in Season; a computer shows them inside Sending email.
  if (sub && (layout === "computer" || key !== "sending" || sub !== "records")) return <Navigate to={key ? `/settings/${key}` : "/settings"} replace />;

  if (!data) {
    return (
      <div className="page">
        <p className="muted">{failed ? "We couldn’t load your settings. Refresh to try again." : "Loading your settings…"}</p>
      </div>
    );
  }
  if (layout !== "computer") return <SeasonSettings layout={layout} pane={key} sub={sub} data={data} run={run} setData={setData} />;
  return key ? <SettingsPage pane={key} data={data} run={run} setData={setData} /> : <SettingsCards data={data} />;
}

function SettingsCards({ data }: { data: SettingsResponse }) {
  const navigate = useNavigate();
  const keys = useKeys();
  const n = setupCount(data.setup);
  const finish =
    n < 4 ? (
      <Button variant="soft" icon="wand" onClick={() => navigate(nextSetupPath(data.setup))}>
        Finish setup, {n} of 4 done
      </Button>
    ) : null;
  return (
    <div className="page">
      <PageHeader title="Settings" sub="Account, billing and team are in Flashsuite." actions={finish} />
      <div className="scards">
        {SNAV.map((p) => {
          const [tone, label] = paneStatus(p.key, data, keys);
          const step = SET_STEP[p.key];
          return (
            // Why a click on the whole card: it is the target people aim at; the heading link is the way in for keys.
            <article key={p.key} className="card scard" onClick={() => navigate(`/settings/${p.key}`)}>
              <div className="row">
                <span className={cx("ic", (p.key === "scripts" || p.key === "api") && "brass")}>
                  <Icon name={p.icon} />
                </span>
                <span className="sp" />
                <Pill tone={tone} dot={tone !== "plain"}>
                  {label}
                </Pill>
              </div>
              <div>
                <h3>
                  <Link to={`/settings/${p.key}`} onClick={(e) => e.stopPropagation()}>
                    {p.label}
                  </Link>
                </h3>
              </div>
              <div className="vis">
                <CardVis k={p.key} data={data} keys={keys} />
                {step && stepUnset(p.key, data) ? (
                  <Button
                    size="s"
                    variant="p"
                    className="mt12"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(step[1]);
                    }}
                  >
                    Finish in setup
                  </Button>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}

/** Whether the workspace key and the read-only key are made, for the API card. */
export function useKeys(): { work: boolean; workKey: ApiKeyInfo | null; read: boolean } | null {
  const [keys, setKeys] = useState<{ work: boolean; workKey: ApiKeyInfo | null; read: boolean } | null>(null);
  useEffect(() => {
    let alive = true;
    Promise.all([api.ingest.key("full").catch(() => null), api.ingest.key("read").catch(() => null)]).then(([w, r]) => {
      if (alive) setKeys({ work: !!w?.prefix, workKey: w, read: !!r?.prefix });
    });
    return () => {
      alive = false;
    };
  }, []);
  return keys;
}

/** The workspace key as the card shows it: its start and its last four, never the whole of it. */
function keyShown(k: ApiKeyInfo | null): string {
  if (k?.key) return `${k.key.slice(0, 9)}••••••••${k.key.slice(-4)}`;
  return k?.prefix ? `${k.prefix}••••••••` : "No key yet";
}

function Swatch({ colour, line = false }: { colour: string; line?: boolean }) {
  return <i style={{ width: 22, height: 22, borderRadius: 6, background: colour, boxShadow: line ? "inset 0 0 0 1px var(--line)" : undefined }} />;
}

function CardVis({ k, data, keys }: { k: PaneKey; data: SettingsResponse; keys: ReturnType<typeof useKeys> }) {
  if (k === "brand") {
    const set = LOOKS.find((l) => l.id === lookOf(data.brand));
    return (
      <div className="row g12">
        <div className="row g4">
          <Swatch colour={set?.accent ?? data.brand.accent} />
          <Swatch colour={set?.brass ?? data.brand.brass} />
          <Swatch colour={set?.paper ?? data.brand.paper} line />
        </div>
        <span className="b6">{lookName(data.brand)}</span>
      </div>
    );
  }
  if (k === "sending") {
    return data.setup.email ? (
      <>
        <div className="b6">{data.sending.from_email}</div>
        <div className="small muted mt4">{sendingHealthLine(data.sending)}</div>
      </>
    ) : (
      <div className="b6 brass">Emails are off</div>
    );
  }
  if (k === "domains") {
    return (
      <>
        <div className="b6">{shownHost(data.domain)}</div>
        <div className="small muted mt4">Opens {opensLabel(data.domain)}</div>
      </>
    );
  }
  if (k === "scripts") {
    const parts = trackParts(data);
    const loaded = parts.filter((p) => p.state === "loaded");
    const waiting = parts.filter((p) => p.state === "waiting");
    if (stepUnset("scripts", data) || (!loaded.length && !waiting.length)) return <div className="small muted">None yet</div>;
    if (loaded.length) {
      return (
        <div className="row g6 wrap">
          {loaded.map((p) => (
            <span key={p.id ?? p.kind} className={cx("tool-on", p.kind === "meta" ? "meta" : p.kind === "ga" ? "ga" : "off")}>
              {p.name}
            </span>
          ))}
        </div>
      );
    }
    return <div className="small muted">{waiting.map((p) => p.name).join(", ")}, waiting</div>;
  }
  if (k === "automations") {
    const team = teamOf(data).length;
    return (
      <>
        <div className="b6">Stopped watching after {data.automations.idle_minutes ?? DEFAULT_IDLE_MINUTES} minutes</div>
        <div className="small muted mt4">
          {team === 0 ? "No one hears about replies yet" : team === 1 ? "1 person hears about replies" : `${team} people hear about replies`}
        </div>
      </>
    );
  }
  return (
    <>
      <div className="mono">{keyShown(keys?.workKey ?? null)}</div>
      <div className="small muted mt4">Read-only key: {keys?.read ? "made" : "not made yet"}</div>
    </>
  );
}

/** One quiet line for a settings page, in the Season list: the same facts its card shows. */
export function paneLine(k: PaneKey, data: SettingsResponse, keys: ReturnType<typeof useKeys>): string {
  if (k === "brand") return lookName(data.brand);
  if (k === "sending") return data.setup.email ? (data.sending.from_email ?? "Emails are off") : "Emails are off";
  if (k === "domains") return shownHost(data.domain);
  if (k === "scripts") {
    const loaded = trackParts(data).filter((p) => p.state === "loaded");
    return loaded.length ? loaded.map((p) => `${p.name} on`).join(", ") : "Not set up";
  }
  if (k === "automations") return `Stopped watching after ${data.automations.idle_minutes ?? DEFAULT_IDLE_MINUTES} minutes`;
  return keys?.read ? "Workspace key and read-only key" : keys?.work ? "Workspace key" : "No key yet";
}

function SettingsPage({ pane, data, run, setData }: { pane: PaneKey; data: SettingsResponse; run: Parameters<typeof BrandPane>[0]["run"]; setData: (d: SettingsResponse) => void }) {
  const navigate = useNavigate();
  const step = SET_STEP[pane];
  let body: ReactNode;
  if (pane === "brand") body = <BrandPane data={data} run={run} />;
  else if (pane === "sending") body = <SendingPane data={data} run={run} />;
  else if (pane === "domains") body = <DomainsPane data={data} run={run} />;
  else if (pane === "scripts") body = <ScriptsPane data={data} run={run} />;
  else if (pane === "automations") body = <AutomationsPane data={data} run={run} setData={setData} />;
  else body = <ApiPane />;
  return (
    <div className={cx("page", pane === "brand" && "wide")}>
      <Link className="back" to="/settings">
        <Icon name="left" />
        Settings
      </Link>
      <div className="slyt">
        <nav className="snav" aria-label="Settings">
            {SNAV.map((p) => {
              const [tone] = paneStatus(p.key, data, null);
              return (
                <Link key={p.key} to={`/settings/${p.key}`} className={cx(pane === p.key && "on")} aria-current={pane === p.key ? "page" : undefined}>
                  <Icon name={p.icon} />
                  {p.label}
                  {tone === "warn" || tone === "bad" ? <span className="st warn" aria-label="Not set up yet" /> : null}
                </Link>
              );
            })}
        </nav>
        <div style={{ minWidth: 0 }}>
          {step && stepUnset(pane, data) ? (
            <div className="note warn" style={{ marginBottom: 18, alignItems: "center" }}>
              <Icon name="info" />
              <div className="sp">
                <b>Not set up yet</b>
              </div>
              <Button size="s" variant="p" onClick={() => navigate(step[1])}>
                Finish in setup
              </Button>
            </div>
          ) : null}
          {body}
        </div>
      </div>
    </div>
  );
}

/** A page's eyebrow and title, inside Settings. */
export function SettingsHead({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div className="eyebrow">{eyebrow}</div>
      <h1 style={{ fontSize: 28, fontWeight: 800, letterSpacing: "-.028em", marginTop: 4 }}>{title}</h1>
    </div>
  );
}
